import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import io from "socket.io-client";
import { useDbContext } from "../../../context/DbContext";
import { useSelector } from "react-redux";
import { selectMenuPermissions } from "../../../app/permissionsSlice";
import { selectActiveLocation } from "../../../app/locationSlice";
import ActionToolbar from "../../NavBars/ActionToolbar";
import SherComboBox from "../../ComboBoxes/SherComboBox";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import { useActiveFyLimits } from "../../../utils/fyDateValidation";

function SchemeRegistrationMob({ onClose }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const permissions = useSelector(selectMenuPermissions);
  const activeLocation = useSelector(selectActiveLocation);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("schemeinfo");
  const [dataFound, setDataFound] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [editDisabled, setEditDisabled] = useState(true);
  const [deleteDisabled, setDeleteDisabled] = useState(true);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [hasReceipts, setHasReceipts] = useState(false);
  const [originalAccountInfo, setOriginalAccountInfo] = useState({ accountName: null, schemeName: null, accountNo: "" });
  
  const [proofFrontPreview, setProofFrontPreview] = useState(null);
  const [proofBackPreview, setProofBackPreview] = useState(null);

  const checkAccountReceipts = async (accId, accNo, autoId, schemeId) => {
    setOriginalAccountInfo({
      accountName: accId !== undefined ? accId : null,
      schemeName: schemeId !== undefined ? schemeId : null,
      accountNo: String(accNo || "").trim(),
    });
    try {
      if (!accId && !accNo && !autoId) {
        setHasReceipts(false);
        return;
      }
      const response = await axios.get(
        `${apiBaseUrl}/main/checkAccountInUse/${dbCode}/${accId || 0}?accountNo=${accNo || ""}&auto=${autoId || ""}`
      );
      if (response.data && (response.data.hasReceipts || response.data.hasRedeem || response.data.inUse)) {
        setHasReceipts(true);
      } else {
        setHasReceipts(false);
      }
    } catch (error) {
      console.error("Error checking account receipts:", error);
      setHasReceipts(false);
    }
  };

  // Refs for all input fields
  const accountNameInputRef = useRef(null);
  const agentNameInputRef = useRef(null);
  const saveBtnRef = useRef(null);
  const editButtonRef = useRef(null);
  const schemeNameInputRef = useRef(null);
  const nomineeNameInputRef = useRef(null);
  const relationInputRef = useRef(null);
  const proofTypeInputRef = useRef(null);
  const idNumberInputRef = useRef(null);
  const nomineeAddress1InputRef = useRef(null);
  const nomineeAddress2InputRef = useRef(null);
  const proofFrontInputRef = useRef(null);
  const proofBackInputRef = useRef(null);
  const entryNoInputRef = useRef(null);
  const durationInputRef = useRef(null);
  const accountNoInputRef = useRef(null);
  const address1InputRef = useRef(null);
  const address2InputRef = useRef(null);
  const cityInputRef = useRef(null);
  const pincodeInputRef = useRef(null);
  const contactPersonInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const telNoInputRef = useRef(null);
  const mobNoInputRef = useRef(null);
  const narrationInputRef = useRef(null);
  const dateInputRef = useRef(null);
  const intperRef = useRef(null);

  const proofFrontBrowseRef = useRef(null);
  const proofBackBrowseRef = useRef(null);

  const updateNavRefs = (refs) => {
    if (refs.saveBtnRef) {
      saveBtnRef.current = refs.saveBtnRef.current;
    } else if (refs.saveButtonRef) {
      saveBtnRef.current = refs.saveButtonRef.current;
    }
    if (refs.editButtonRef) {
      editButtonRef.current = refs.editButtonRef.current;
    }
  };

  const fieldRefMap = {
    entryNo: entryNoInputRef,
    date: dateInputRef,
    schemeName: schemeNameInputRef,
    accountName: accountNameInputRef,
    agent: agentNameInputRef,
    duration: durationInputRef,
    intperc: intperRef,
    accountNo: accountNoInputRef,

    address1: address1InputRef,
    address2: address2InputRef,
    city: cityInputRef,
    pincode: pincodeInputRef,
    contactPerson: contactPersonInputRef,
    email: emailInputRef,
    telNo: telNoInputRef,
    mobNo: mobNoInputRef,

    nomineeName: nomineeNameInputRef,
    relation: relationInputRef,
    proofType: proofTypeInputRef,
    idNumber: idNumberInputRef,
    nomineeAddress1: nomineeAddress1InputRef,
    nomineeAddress2: nomineeAddress2InputRef,
    proofFrontBrowse: proofFrontBrowseRef,
    proofBackBrowse: proofBackBrowseRef,
    narration: narrationInputRef,
  };

  const fieldTabMap = {
    entryNo: "schemeinfo",
    date: "schemeinfo",
    schemeName: "schemeinfo",
    accountName: "schemeinfo",
    agent: "schemeinfo",
    duration: "schemeinfo",
    intperc: "schemeinfo",
    accountNo: "schemeinfo",

    address1: "address",
    address2: "address",
    city: "address",
    pincode: "address",
    contactPerson: "address",
    email: "address",
    telNo: "address",
    mobNo: "address",

    nomineeName: "nominee",
    relation: "nominee",
    proofType: "nominee",
    idNumber: "nominee",
    nomineeAddress1: "nominee",
    nomineeAddress2: "nominee",
    proofFrontBrowse: "nominee",
    proofBackBrowse: "nominee",
    narration: "nominee",
  };

  const fieldOrder = [
    "entryNo",
    "date",
    "schemeName",
    "accountName",
    "agent",
    "duration",
    "intperc",
    "accountNo",
    "address1",
    "address2",
    "city",
    "pincode",
    "contactPerson",
    "email",
    "telNo",
    "mobNo",
    "nomineeName",
    "relation",
    "proofType",
    "idNumber",
    "nomineeAddress1",
    "nomineeAddress2",
    "narration",
  ];

  const focusFieldById = (id, attempt = 0) => {
    const ref = fieldRefMap[id];
    const tab = fieldTabMap[id];
    if (!ref) return;

    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }

    requestAnimationFrame(() => {
      if (ref.current) {
        if (typeof ref.current.focusInput === "function") {
          ref.current.focusInput();
        } else if (typeof ref.current.focus === "function") {
          ref.current.focus();
        }
      } else if (attempt < 15) {
        setTimeout(() => focusFieldById(id, attempt + 1), 25);
      }
    });
  };

  const focusSaveOrEdit = () => {
    const isSaveEnabled = !(dataFound || isLoading || saveDisabled);
    const isEditEnabled = !(!dataFound || editDisabled || isLoading);

    if (isSaveEnabled && saveBtnRef.current) {
      saveBtnRef.current.focus();
    } else if (isEditEnabled && editButtonRef.current) {
      editButtonRef.current.focus();
    }
  };

  const handleEnterNav = (event, currentId) => {
    if (event.key !== "Enter" && event.key !== "Return") return;
    event.preventDefault();

    const idx = fieldOrder.indexOf(currentId);
    if (idx === -1) return;

    const nextId = fieldOrder[idx + 1];
    if (nextId) {
      focusFieldById(nextId);
    } else {
      focusSaveOrEdit();
    }
  };

  const { minDate, maxDate, isDateInFy } = useActiveFyLimits();

  const getCurrentDate = () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");
    const formattedToday = `${year}-${month}-${day}`;
    if (minDate && formattedToday < minDate) return minDate;
    if (maxDate && formattedToday > maxDate) return maxDate;
    return formattedToday;
  };

  const today = getCurrentDate();

  const [schemeRegData, setschemeRegData] = useState({
    entryNo: "",
    date: today,
    schemeName: { value: "", label: "" },
    type: "",
    accountName: { value: "", label: "" },
    agent: { value: "", label: "" },
    duration: "",
    accountNo: "",
    add1: "",
    add2: "",
    city: "",
    pincode: "",
    contactPerson: "",
    mobile: "",
    email: "",
    tele: "",
    narration: "",
    amount: 0,
    rate: "",
    gm: 0.0,
    intperc: "",
    nomineeName: "",
    relation: "",
    proofType: { value: "", label: "" },
    idNumber: "",
    nomineeAdd1: "",
    nomineeAdd2: "",
    proofFrontFile: null,
    proofBackFile: null,
    proofFrontFileName: "",
    proofBackFileName: "",
    proofFrontBase64: "",
    proofBackBase64: "",
    isActive: true,
    Location: activeLocation ? activeLocation.id : "",
    auto: "",
  });

  const [sch_entryno, setSchEntryNo] = useState("");
  const [customernames, setCustomersNames] = useState([]);
  const [AcNames, setANames] = useState([]);
  const [employeeNames, setEmployeeNames] = useState([]);
  const [regcustomers, setregcustomers] = useState([]);
  const [firstEno, setFirstEno] = useState("");
  const [lastEno, setLastEno] = useState("");
  const [countExist, setcountExist] = useState("");
  const [gmvalue, setgmvalue] = useState("");
  const [schemeNames, setschemeNames] = useState([]);

  const handleFileChange = async (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File size should be less than 5MB");
        return;
      }

      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        alert("Only JPG, PNG, and PDF files are allowed");
        return;
      }

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (fileType === 'front') {
            setProofFrontPreview(e.target.result);
          } else {
            setProofBackPreview(e.target.result);
          }
        };
        reader.readAsDataURL(file);
      } else {
        if (fileType === 'front') {
          setProofFrontPreview(null);
        } else {
          setProofBackPreview(null);
        }
      }

      const base64 = await convertToBase64(file);

      if (fileType === 'front') {
        setschemeRegData((prevData) => ({
          ...prevData,
          proofFrontFile: file,
          proofFrontFileName: file.name,
          proofFrontBase64: base64,
        }));
      } else {
        setschemeRegData((prevData) => ({
          ...prevData,
          proofBackFile: file,
          proofBackFileName: file.name,
          proofBackBase64: base64,
        }));
      }
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleRemoveFile = (fileType) => {
    if (fileType === 'front') {
      setschemeRegData((prevData) => ({
        ...prevData,
        proofFrontFile: null,
        proofFrontFileName: "",
        proofFrontBase64: "",
      }));
      setProofFrontPreview(null);
      if (proofFrontInputRef.current) {
        proofFrontInputRef.current.value = "";
      }
    } else {
      setschemeRegData((prevData) => ({
        ...prevData,
        proofBackFile: null,
        proofBackFileName: "",
        proofBackBase64: "",
      }));
      setProofBackPreview(null);
      if (proofBackInputRef.current) {
        proofBackInputRef.current.value = "";
      }
    }
  };

  const showFilePreview = (fileType) => {
    let fileData = null;
    let fileName = "";

    if (fileType === 'front') {
      fileData = schemeRegData.proofFrontBase64;
      fileName = schemeRegData.proofFrontFileName;
    } else {
      fileData = schemeRegData.proofBackBase64;
      fileName = schemeRegData.proofBackFileName;
    }

    if (!fileData) {
      alert("No file to preview");
      return;
    }

    const previewWindow = window.open('', '_blank');
    previewWindow.document.write(`
      <html>
        <head>
          <title>Preview - ${fileName}</title>
          <style>
            body { margin: 0; padding: 20px; display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 100vh; background: #f5f5f5; }
            img { max-width: 90vw; max-height: 80vh; box-shadow: 0 4px 8px rgba(0,0,0,0.2); margin-bottom: 20px; }
            .pdf-message { font-family: Arial, sans-serif; padding: 20px; background: white; border-radius: 8px; box-shadow: 0 4px 8px rgba(0,0,0,0.2); text-align: center; }
            .button-container { margin-top: 20px; display: flex; gap: 10px; }
            .download-btn { padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer; text-decoration: none; }
            .download-btn:hover { background: #0056b3; }
            .close-btn { padding: 10px 20px; background: #6c757d; color: white; border: none; border-radius: 4px; cursor: pointer; }
            .close-btn:hover { background: #545b62; }
          </style>
        </head>
        <body>
          ${fileName.endsWith('.pdf')
        ? `<div class="pdf-message">
                 <h3>PDF File Preview</h3>
                 <p>File: ${fileName}</p>
                 <p>PDF files cannot be previewed inline.</p>
                 <div class="button-container">
                   <a class="download-btn" href="data:application/pdf;base64,${fileData}" download="${fileName}">Download PDF</a>
                   <button class="close-btn" onclick="window.close()">Close</button>
                 </div>
               </div>`
        : `<img src="data:image/jpeg;base64,${fileData}" alt="${fileName}" />
               <div class="button-container">
                 <a class="download-btn" href="data:image/jpeg;base64,${fileData}" download="${fileName}">Download Image</a>
                 <button class="close-btn" onclick="window.close()">Close</button>
               </div>`}
        </body>
      </html>
    `);
    previewWindow.document.close();
  };

  const downloadFile = (fileType) => {
    let fileData = null;
    let fileName = "";
    let mimeType = "";

    if (fileType === 'front') {
      fileData = schemeRegData.proofFrontBase64;
      fileName = schemeRegData.proofFrontFileName;
      mimeType = fileName.endsWith('.pdf') ? 'application/pdf' : 'image/jpeg';
    } else {
      fileData = schemeRegData.proofBackBase64;
      fileName = schemeRegData.proofBackFileName;
      mimeType = fileName.endsWith('.pdf') ? 'application/pdf' : 'image/jpeg';
    }

    if (!fileData || !fileName) {
      alert("No file available to download");
      return;
    }

    const link = document.createElement('a');
    link.href = `data:${mimeType};base64,${fileData}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const calculateGm = () => {
    const amount = parseFloat(schemeRegData.amount);
    const rate = parseFloat(schemeRegData.rate);

    if (!isNaN(amount) && !isNaN(rate) && rate !== 0) {
      const gmValue = amount / rate;
      setgmvalue(gmValue);
      setschemeRegData((prevData) => ({
        ...prevData,
        gm: gmValue.toFixed(3),
      }));
    } else {
      setschemeRegData((prevData) => ({
        ...prevData,
        gm: "",
      }));
    }
  };

  const fetchSchCurrentRate = async (targetDate) => {
    try {
      const dateToUse = targetDate || schemeRegData.date;
      const response = await axios.get(`${apiBaseUrl}/main/getGoldRate/${dbCode}?date=${dateToUse || ""}`);
      const currentrate = response.data[0]?.currentRate;
      setschemeRegData((prevData) => ({ ...prevData, rate: currentrate || 0 }));
    } catch (error) {
      console.error("Error fetching current rate:", error.message);
    }
  };

  const fetchupdschregisteredcustomers = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/main/registeredcustomers/${dbCode}`);
      setregcustomers(response.data);
    } catch (error) {
      console.error("Error fetching sch reg names:", error.message);
    }
  };

  const fetchSchEntryno = async (targetSchemeId = null) => {
    try {
      const sId = targetSchemeId || schemeRegData.schemeName?.value || null;
      const url = sId 
        ? `${apiBaseUrl}/main/Sch_entryno/${dbCode}?schemeId=${sId}`
        : `${apiBaseUrl}/main/Sch_entryno/${dbCode}`;
      const response = await axios.get(url);
      const firstItem = response.data?.[0];
      const nextEntry = firstItem?.entryNo ?? firstItem?.[""] ?? 1;
      const formattedAcc = firstItem?.formattedAccNo;
      const nextAcc = firstItem?.nextAccNo ?? nextEntry;
      setschemeRegData((prevData) => ({
        ...prevData,
        entryNo: nextEntry,
        accountNo: !dataFound ? (formattedAcc || String(nextAcc).padStart(3, "0")) : prevData.accountNo,
      }));
      setSchEntryNo(nextEntry);
    } catch (error) {
      console.error("Error fetching entry no:", error.message);
    }
  };

  const fetchSchemes = async () => {
    try {
      const response = await axios.get(
        `${apiBaseUrl}/main/getSchemes/${dbCode}`
      );

      if (response.data.success) {
        const formattedSchemes = response.data.data.map((item) => ({
          value: item.SchemeId,
          label: item.SchemeName,
          duration: item.Duration,
          type: item.SchemeType || "Monthly",
        }));

        setschemeNames(formattedSchemes);
      }
    } catch (error) {
      console.error("Error fetching schemes:", error);
    }
  };

  const handleSchemeSelect = async (opt) => {
    if (!opt) {
      setschemeRegData((prevData) => ({
        ...prevData,
        schemeName: { value: "", label: "" },
        duration: "",
        type: "",
      }));
      if (!dataFound) {
        fetchSchEntryno(null);
      }
      return;
    }

    setschemeRegData((prevData) => ({
      ...prevData,
      schemeName: opt,
      duration: opt.duration !== undefined && opt.duration !== null ? opt.duration : prevData.duration,
      type: opt.type || prevData.type || "Monthly",
    }));

    try {
      let schemeDetail = null;
      if (opt.value) {
        const res = await axios.get(`${apiBaseUrl}/main/findSchemeById/${dbCode}/${opt.value}`);
        if (res.data?.success && res.data.data) {
          schemeDetail = res.data.data;
        }
      }
      if (!schemeDetail && opt.label) {
        const res = await axios.get(`${apiBaseUrl}/main/findSchemeByName/${dbCode}/${encodeURIComponent(opt.label)}`);
        if (res.data?.success && res.data.data) {
          schemeDetail = res.data.data;
        }
      }
      if (schemeDetail) {
        setschemeRegData((prevData) => ({
          ...prevData,
          duration: schemeDetail.Duration !== undefined && schemeDetail.Duration !== null ? schemeDetail.Duration : prevData.duration,
          type: schemeDetail.SchemeType || schemeDetail.type || prevData.type || "Monthly",
        }));
      }
    } catch (err) {
      console.error("Error fetching scheme details:", err);
    }
  };

  useEffect(() => {
    calculateGm();
  }, [schemeRegData.amount, schemeRegData.rate]);

  useEffect(() => {
    fetchSchEntryno();

    const fetchAccountNames = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/main/accountNames/${dbCode}`);
        const schemeDet = response.data;
        const AccNames = schemeDet.map((item) => ({
          value: item.Ledcode,
          label: item.LedName
        }));
        const cnames = schemeDet.map((item) => item.Ledcode);
        setANames(AccNames);
        setCustomersNames(cnames);
      } catch (error) {
        console.error("Error fetching names Values:", error.message);
      }
    };

    const fetchEmployeeNames = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/main/SchReg_empname/${dbCode}`);
        const empNames = response.data.map((item) => ({
          value: item.Auto,
          label: item.name
        }));
        setEmployeeNames(empNames);
      } catch (error) {
        console.error("Error fetching employee names:", error.message);
      }
    };

    fetchAccountNames();
    fetchEmployeeNames();
    fetchupdschregisteredcustomers();
    fetchSchCurrentRate(schemeRegData.date);
    fetchSchemes();

    const socket = io(apiBaseUrl);
    socket.on("entrynoUpdated", () => {
      fetchSchEntryno();
    });
    socket.on("LedgerUpdated", () => {
      fetchAccountNames();
    });
    socket.on("SchemeUpdated", () => {
      fetchSchemes();
      setschemeRegData((prevData) => {
        if (prevData.schemeName?.value) {
          axios
            .get(`${apiBaseUrl}/main/findSchemeById/${dbCode}/${prevData.schemeName.value}`)
            .then((res) => {
              if (res.data?.success && res.data.data) {
                const schemeDetail = res.data.data;
                setschemeRegData((innerPrev) => ({
                  ...innerPrev,
                  duration: schemeDetail.Duration !== undefined && schemeDetail.Duration !== null ? schemeDetail.Duration : innerPrev.duration,
                  type: schemeDetail.SchemeType || schemeDetail.type || innerPrev.type || "Monthly",
                }));
              }
            })
            .catch(console.error);
        }
        return prevData;
      });
    });

    return () => socket.disconnect();
  }, [apiBaseUrl, dbCode]);

  useEffect(() => {
    setTimeout(() => {
      if (entryNoInputRef.current) {
        entryNoInputRef.current.focus();
      }
    }, 100);
  }, []);

  useEffect(() => {
    if (schemeRegData.date && dbCode) {
      fetchSchCurrentRate(schemeRegData.date);
    }
  }, [schemeRegData.date, dbCode, apiBaseUrl]);

  const fetchcustomerdetails = async (accountId) => {
    if (!accountId) return;

    try {
      const response = await axios.get(`${apiBaseUrl}/main/scheRegcustomerDetails/${accountId}/${dbCode}`);
      if (response.status === 200) {
        const foundData = response.data[0];
        const matchAcc = AcNames.find((item) => item.value === foundData.Ledcode);
        setschemeRegData({
          ...schemeRegData,
          accountName: matchAcc ? { value: matchAcc.value, label: matchAcc.label } : { value: foundData.Ledcode, label: "" },
          add1: foundData.add1,
          add2: foundData.add2,
          pincode: foundData.PinNo,
          mobile: foundData.Mobile,
          email: foundData.Email,
          tele: foundData.Telno,
        });
      }
    } catch (error) {
      console.error("Error fetching ledger datas:", error.message);
    }
  };

  const handleClear = () => {
    setschemeRegData({
      entryNo: sch_entryno,
      date: getCurrentDate(),
      schemeName: { value: "", label: "" },
      type: "",
      accountName: { value: "", label: "" },
      agent: { value: "", label: "" },
      duration: "",
      accountNo: "",
      add1: "",
      add2: "",
      city: "",
      pincode: "",
      contactPerson: "",
      mobile: "",
      email: "",
      tele: "",
      narration: "",
      amount: 0,
      intperc: "",
      nomineeName: "",
      relation: "",
      proofType: { value: "", label: "" },
      idNumber: "",
      nomineeAdd1: "",
      nomineeAdd2: "",
      proofFrontFile: null,
      proofBackFile: null,
      proofFrontFileName: "",
      proofBackFileName: "",
      proofFrontBase64: "",
      proofBackBase64: "",
      isActive: true,
      Location: activeLocation ? activeLocation.id : "",
      auto: "",
    });

    setProofFrontPreview(null);
    setProofBackPreview(null);
    setDataFound(false);
    setEditDisabled(true);
    setDeleteDisabled(true);
    setSaveDisabled(false);
    setHasReceipts(false);
    setOriginalAccountInfo({ accountName: null, schemeName: null, accountNo: "" });
    setActiveTab('schemeinfo');

    setTimeout(() => {
      if (entryNoInputRef.current) {
        entryNoInputRef.current.focus();
      }
    }, 100);
  };

  const handleClearFind = () => {
    setschemeRegData({
      accountName: { value: "", label: "" },
      agent: { value: "", label: "" },
      duration: "",
      type: "",
      accountNo: "",
      add1: "",
      add2: "",
      city: "",
      pincode: "",
      contactPerson: "",
      mobile: "",
      email: "",
      tele: "",
      narration: "",
      amount: 0,
      intperc: "",
      nomineeName: "",
      relation: "",
      proofType: { value: "", label: "" },
      idNumber: "",
      nomineeAdd1: "",
      nomineeAdd2: "",
      proofFrontFileName: "",
      proofBackFileName: "",
      proofFrontBase64: "",
      proofBackBase64: "",
      isActive: true,
      Location: activeLocation ? activeLocation.id : "",
      auto: "",
    });

    setProofFrontPreview(null);
    setProofBackPreview(null);
    setHasReceipts(false);
    setOriginalAccountInfo({ accountName: null, schemeName: null, accountNo: "" });
    setActiveTab('schemeinfo');

    setTimeout(() => {
      if (entryNoInputRef.current) {
        entryNoInputRef.current.focus();
      }
    }, 100);

    setEditDisabled(true);
    setDeleteDisabled(true);
    setSaveDisabled(false);
  };

  const validateEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const checkDuplicateInSameScheme = (schemeId, accountId, currentEntryNo = null) => {
    if (!schemeId || !accountId) return false;

    return regcustomers.some(customer =>
      customer.accname === accountId &&
      customer.schemeId === schemeId &&
      (currentEntryNo === null || customer.entryno !== currentEntryNo)
    );
  };

  const handleSubmit = async () => {
    setSaveDisabled(true);
    try {
      if (schemeRegData.mobile && schemeRegData.mobile.length !== 10 && schemeRegData.mobile.length !== 0) {
        alert("Mobile number must be 10 digits.");
        setSaveDisabled(false);
        return;
      }

      const selectedSchemeId = schemeRegData.schemeName?.value;
      if (!selectedSchemeId) {
        alert("Please select Scheme Name.");
        setSaveDisabled(false);
        return;
      }

      const selectedAccountId = schemeRegData.accountName?.value;
      if (!selectedAccountId) {
        alert("Please enter account name.");
        setSaveDisabled(false);
        return;
      }

      if (schemeRegData.accountNo === "") {
        alert("Please enter account no.");
        setSaveDisabled(false);
        return;
      }

      const isAccNoDuplicate = regcustomers.some(
        (customer) => String(customer.accno || "").trim().toLowerCase() === String(schemeRegData.accountNo).trim().toLowerCase()
      );
      if (isAccNoDuplicate) {
        alert(`Account Number "${schemeRegData.accountNo}" is already in use. Duplicate account numbers are not allowed.`);
        setSaveDisabled(false);
        return;
      }

      if (schemeRegData.email && !validateEmail(schemeRegData.email)) {
        alert("Invalid email address.");
        setSaveDisabled(false);
        return;
      }

      if (!customernames.includes(selectedAccountId)) {
        alert("Customer is not registered.");
        setSaveDisabled(false);
        return;
      }

      const isDuplicateInSameScheme = checkDuplicateInSameScheme(selectedSchemeId, selectedAccountId);

      if (isDuplicateInSameScheme) {
        alert(`This account name is already registered for the selected scheme. Duplicate account names are not allowed within the same scheme.`);
        setSaveDisabled(false);
        return;
      }

      const updatedSchemeRegData = {
        ...schemeRegData,
        agent: schemeRegData.agent?.value || 0,
        accountName: schemeRegData.accountName?.value,
        schemeName: schemeRegData.schemeName?.value,
        proofFrontBase64: schemeRegData.proofFrontBase64,
        proofBackBase64: schemeRegData.proofBackBase64,
        proofType: schemeRegData.proofType?.label || schemeRegData.proofType?.value || "",
        isActive: schemeRegData.isActive ? 1 : 0,
      };

      const response = await axios.post(`${apiBaseUrl}/main/insertScheme/${dbCode}`, updatedSchemeRegData);

      if (response.data.success) {
        alert("Account registered successfully!");
        handleClear();
        fetchSchEntryno();
      } else {
        if (response.data.message === "Duplicate account number") {
          alert("Duplicate account number.");
        } else {
          alert("Error submitting data. Please try again.");
        }
        setSaveDisabled(false);
      }
      await fetchupdschregisteredcustomers();
    } catch (error) {
      setSaveDisabled(false);
      console.error("Error:", error.message);
      alert("An error occurred. Please try again.");
    }
  };

  const handleFind = async () => {
    handleClearFind();
    try {
      const selectedAccountId = schemeRegData.accountName?.value;

      if (regcustomers.some(customer => customer.accname === selectedAccountId)) {
        const response = await axios.get(`${apiBaseUrl}/main/findbyAccname/${selectedAccountId}/${dbCode}`);

        if (response.status === 200) {
          const foundData = response.data[0];
          const formattedDate = new Date(foundData.Date).toISOString().split("T")[0];

          if (foundData.proofFrontBase64) {
            setProofFrontPreview(`data:image/jpeg;base64,${foundData.proofFrontBase64}`);
          }
          if (foundData.proofBackBase64) {
            setProofBackPreview(`data:image/jpeg;base64,${foundData.proofBackBase64}`);
          }

          const matchScheme = schemeNames.find((item) => item.value === foundData.schemeId);
          const matchAcc = AcNames.find((item) => item.value === foundData.accname);
          const matchAgent = employeeNames.find((item) => item.value === foundData.agent);

          setschemeRegData({
            ...schemeRegData,
            entryNo: foundData.entryno,
            date: formattedDate,
            schemeName: matchScheme ? { value: matchScheme.value, label: matchScheme.label } : { value: foundData.schemeId, label: "" },
            accountName: matchAcc ? { value: matchAcc.value, label: matchAcc.label } : { value: foundData.accname, label: "" },
            agent: matchAgent ? { value: matchAgent.value, label: matchAgent.label } : { value: foundData.agent, label: "" },
            duration: foundData.duration,
            type: foundData.type || matchScheme?.type || "",
            accountNo: foundData.accno,
            add1: foundData.Address1,
            add2: foundData.Address2,
            city: foundData.City,
            pincode: foundData.pin,
            contactPerson: foundData.contactperson,
            mobile: foundData.mobileNo,
            email: foundData.email,
            tele: foundData.phoneNo,
            narration: foundData.NARRATION,
            amount: foundData.Amount,
            rate: foundData.rate,
            gm: foundData.grm,
            intperc: foundData.intperc,
            nomineeName: foundData.nomineeName || "",
            relation: foundData.relation || "",
            proofType: { value: foundData.proofType, label: foundData.proofType || "" },
            idNumber: foundData.idNumber || "",
            nomineeAdd1: foundData.nomineeAdd1 || "",
            nomineeAdd2: foundData.nomineeAdd2 || "",
            proofFrontFileName: foundData.proofFrontFileName || "",
            proofBackFileName: foundData.proofBackFileName || "",
            proofFrontBase64: foundData.proofFrontBase64 || "",
            proofBackBase64: foundData.proofBackBase64 || "",
            isActive: foundData.isActive === 1 || foundData.isActive === true,
          auto: foundData.auto || "",
          });

          setDataFound(true);
          setEditDisabled(false);
          setDeleteDisabled(false);
          setSaveDisabled(true);
          checkAccountReceipts(foundData.accname, foundData.accno, foundData.auto, foundData.schemeId);
        } else {
          console.error("Error retrieving data:", response.data);
          setDataFound(false);
        }
      } else {
        if (!selectedAccountId) {
          alert("Select an Account Name");
        } else {
          alert("Account Not Registered");
        }
      }
    } catch (error) {
      console.error("Error:", error.message);
      setDataFound(false);
    }
  };

  const handleSearchByAccountNo = async () => {
    handleClearFind();
    try {
      const response = await axios.get(`${apiBaseUrl}/main/findbyAccno/${dbCode}`, {
        params: { accNo: schemeRegData.accountNo }
      });
      if (response.status === 200) {
        const foundData = response.data[0];
        const formattedDate = new Date(foundData.Date).toISOString().split("T")[0];

        if (foundData.proofFrontBase64) {
          setProofFrontPreview(`data:image/jpeg;base64,${foundData.proofFrontBase64}`);
        }
        if (foundData.proofBackBase64) {
          setProofBackPreview(`data:image/jpeg;base64,${foundData.proofBackBase64}`);
        }

        const matchScheme = schemeNames.find((item) => item.value === foundData.schemeId);
        const matchAcc = AcNames.find((item) => item.value === foundData.accname);
        const matchAgent = employeeNames.find((item) => item.value === foundData.agent);

        setschemeRegData({
          ...schemeRegData,
          entryNo: foundData.entryno,
          date: formattedDate,
          schemeName: matchScheme ? { value: matchScheme.value, label: matchScheme.label } : { value: foundData.schemeId, label: "" },
          accountName: matchAcc ? { value: matchAcc.value, label: matchAcc.label } : { value: foundData.accname, label: "" },
          agent: matchAgent ? { value: matchAgent.value, label: matchAgent.label } : { value: foundData.agent, label: "" },
          duration: foundData.duration,
          type: foundData.type || matchScheme?.type || "",
          accountNo: foundData.accno,
          add1: foundData.Address1,
          add2: foundData.Address2,
          city: foundData.City,
          pincode: foundData.pin,
          contactPerson: foundData.contactperson,
          mobile: foundData.mobileNo,
          email: foundData.email,
          tele: foundData.phoneNo,
          narration: foundData.NARRATION,
          amount: foundData.Amount,
          rate: foundData.rate,
          gm: foundData.grm,
          intperc: foundData.intperc,
          nomineeName: foundData.nomineeName || "",
          relation: foundData.relation || "",
          proofType: { value: foundData.proofType, label: foundData.proofType || "" },
          idNumber: foundData.idNumber || "",
          nomineeAdd1: foundData.nomineeAdd1 || "",
          nomineeAdd2: foundData.nomineeAdd2 || "",
          proofFrontFileName: foundData.proofFrontFileName || "",
          proofBackFileName: foundData.proofBackFileName || "",
          proofFrontBase64: foundData.proofFrontBase64 || "",
          proofBackBase64: foundData.proofBackBase64 || "",
          isActive: foundData.isActive === 1 || foundData.isActive === true,
          auto: foundData.auto || "",
        });
        setDataFound(true);
        setEditDisabled(false);
        setDeleteDisabled(false);
        setSaveDisabled(true);
        checkAccountReceipts(foundData.accname, foundData.accno, foundData.auto, foundData.schemeId);
      }
    } catch (error) {
      console.error("Error:", error.message);
      setDataFound(false);
    }
  };

  const handleUpdate = async () => {
    try {
      const mobileNumber = schemeRegData.mobile ? schemeRegData.mobile.toString() : "";
      if (schemeRegData.mobile && mobileNumber.length !== 10 && mobileNumber.length !== 0) {
        alert("Invalid Mobile Number.");
        return;
      }
      if (schemeRegData.email && !validateEmail(schemeRegData.email)) {
        alert("Invalid email address");
        return;
      }

      const selectedSchemeId = schemeRegData.schemeName?.value;
      if (!selectedSchemeId) {
        alert("Please select Scheme Name.");
        return;
      }

      if (dataFound === true) {
        const selectedAccountId = schemeRegData.accountName?.value;

        if (hasReceipts) {
          const curAccName = schemeRegData.accountName?.value;
          const curSchemeId = schemeRegData.schemeName?.value;
          const curAccNo = String(schemeRegData.accountNo || "").trim();

          const isAccNameChanged = originalAccountInfo.accountName !== null && curAccName !== originalAccountInfo.accountName;
          const isSchemeChanged = originalAccountInfo.schemeName !== null && curSchemeId !== originalAccountInfo.schemeName;
          const isAccNoChanged = originalAccountInfo.accountNo !== "" && curAccNo !== originalAccountInfo.accountNo;

          if (isAccNameChanged || isSchemeChanged || isAccNoChanged) {
            alert("Cannot change Scheme Name, Account Name, or Account Number because receipts already exist for this account.");
            return;
          }
        }

        const isDuplicateInSameScheme = checkDuplicateInSameScheme(
          selectedSchemeId,
          selectedAccountId,
          schemeRegData.entryNo
        );

        if (isDuplicateInSameScheme) {
          alert(`This account name is already registered for the selected scheme. Duplicate account names are not allowed within the same scheme.`);
          return;
        }

        const isAccNoDuplicateUpd = regcustomers.some(
          (customer) =>
            String(customer.accno || "").trim().toLowerCase() === String(schemeRegData.accountNo).trim().toLowerCase() &&
            customer.entryno !== schemeRegData.entryNo
        );
        if (isAccNoDuplicateUpd) {
          alert(`Account Number "${schemeRegData.accountNo}" is already in use by another registration.`);
          return;
        }

        const updatedSchemeRegData = {
          ...schemeRegData,
          agent: schemeRegData.agent?.value || 0,
          accountName: schemeRegData.accountName?.value,
          schemeName: schemeRegData.schemeName?.value,
          proofFrontBase64: schemeRegData.proofFrontBase64,
          proofBackBase64: schemeRegData.proofBackBase64,
          proofType: schemeRegData.proofType?.label || schemeRegData.proofType?.value || "",
          isActive: schemeRegData.isActive ? 1 : 0,
          // Send closedDate: today's date when closing, null when re-activating
          closedDate: schemeRegData.isActive ? null : new Date().toISOString().split('T')[0],
        };

        const response = await axios.post(`${apiBaseUrl}/main/updateScheme/${dbCode}`, updatedSchemeRegData);

        if (response.status === 200) {
          alert("Account Updated Successfully!");
          handleClear();
        } else {
          console.error("Error submitting data:", response.data);
          alert("Error updating data. Please try again.");
        }
      }
      await fetchupdschregisteredcustomers();
    } catch (error) {
      console.error("Error:", error.message);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert("An error occurred while updating. Please try again.");
      }
    }
  };

  const handleDelete = async () => {
    try {
      const selectedAccountId = schemeRegData.accountName?.value;
      const accountNo = schemeRegData.accountNo;
      const autoId = schemeRegData.auto;

      if (!selectedAccountId) {
        alert("please select an Account Name");
        return;
      }

      // Check if account has receipts or redemption before prompting delete
      try {
        const checkUrl = `${apiBaseUrl}/main/checkAccountInUse/${dbCode}/${selectedAccountId}?accountNo=${accountNo || ''}&auto=${autoId || ''}`;
        const checkRes = await axios.get(checkUrl);
        if (checkRes.data && checkRes.data.inUse) {
          alert("Cannot delete! Receipts or redemption already exist for this account.");
          return;
        }
      } catch (err) {
        console.error("Error checking account usage:", err);
      }

      if (!window.confirm("Do You Want To Delete..?")) {
        return;
      }

      const response = await axios.delete(
        `${apiBaseUrl}/main/deletebyAccname/${selectedAccountId}/${dbCode}?locationId=${activeLocation ? activeLocation.id : ''}&accountNo=${accountNo || ''}&auto=${autoId || ''}`
      );
      if (response.status === 200 && response.data.success && response.data.affectedRows > 0) {
        alert("Account Deleted Successfully!");
        handleClear();
      } else {
        console.error("Error deleting data:", response.data);
        alert(response.data?.message || "Error deleting data: Location mismatch or account not found.");
      }
      await fetchupdschregisteredcustomers();
    } catch (error) {
      console.error("Error:", error.message);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert("An error occurred while deleting. Please try again.");
      }
    }
  };

  const handleCheckCustomer = (e) => {
    const targetValue = e.target.value;
    const isValuePresent = AcNames.some((item) => item.label === targetValue);

    if (!isValuePresent && e.target.value !== "") {
      alert("Customer Not Registered");
    }
  };

  const handleCheckScheme = (e) => {
    const targetValue = e.target.value;
    const matched = schemeNames.find((item) => item.label?.toLowerCase() === targetValue?.toLowerCase());

    if (matched) {
      handleSchemeSelect(matched);
      return;
    }

    if (!matched && e.target.value !== "") {
      alert("Scheme Not Registered");
    }
  };

  const proofTypes = [
    { value: "Aadhaar", label: "Aadhaar Card" },
    { value: "VoterID", label: "Voter ID" },
    { value: "PAN", label: "PAN Card" },
    { value: "Driving", label: "Driving License" },
    { value: "Passport", label: "Passport" },
    { value: "Other", label: "Other" },
  ];

  // Toggle function for Active/Closed status
  const toggleAccountStatus = () => {
    setschemeRegData(prev => ({
      ...prev,
      isActive: !prev.isActive,
      // Set closedDate to today when closing; clear when re-activating
      closedDate: prev.isActive ? new Date().toISOString().split('T')[0] : null,
    }));
  };

  return (
    <div className="fixed inset-0 w-full h-full bg-[#f0f0f0] flex flex-col pointer-events-auto z-[10000] overflow-y-auto font-sans">
      <WindowHeader
        title="Scheme Customer Registration"
        logo={headeLogo}
        onClose={onClose || (() => navigate("/home"))}
        className="bg-[rgb(0,8,169)] rounded-none"
        closeBtnClassName="w-6 h-6 border-none rounded-none mr-2"
      />
      {/* Action Toolbar */}
      <div className="w-full flex-shrink-0">
        <ActionToolbar
          width="100%"
          height="auto"
          bgColor="#EAF0FC"
          showSave={true}
          showPrint={false}
          showClear={true}
          showFind={true}
          showEdit={true}
          showDelete={true}
          showExit={true}
          showNavigation={false}
          saveDisabled={dataFound || isLoading || saveDisabled}
          editDisabled={!dataFound || editDisabled || isLoading}
          deleteDisabled={!dataFound || deleteDisabled || isLoading}
          dataFound={dataFound}
          permissions={permissions}
          permissionKey="schemeReg"
          onSave={handleSubmit}
          onClear={handleClear}
          onFind={handleFind}
          onEdit={handleUpdate}
          onDelete={handleDelete}
          onExit={onClose || (() => navigate("/home"))}
          onRefsUpdate={updateNavRefs}
        />
      </div>

      {/* Tab Navigation */}
      <div className="w-full flex-shrink-0 border-b border-[#b8cce0] bg-white">
        <div className="flex justify-between px-1 pt-2.5 overflow-x-auto gap-1">
          {[
            { id: "schemeinfo", label: "Scheme Info" },
            { id: "address", label: "Address" },
            { id: "nominee", label: "Nominee & Proof" }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2.5 text-xs font-bold border-none cursor-pointer transition-all duration-150 flex-1 text-center whitespace-nowrap
                ${activeTab === tab.id
                  ? "bg-[#E8EEF8] text-[#0008a9] rounded-t-lg border-b-4 border-[#0008a9]"
                  : "bg-transparent text-[#1a2e4a] hover:bg-[#1a2e4a14]"
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content Body */}
      <div className="flex-1 p-4 bg-[#f8fafc] overflow-y-auto pb-20">
        {activeTab === "schemeinfo" && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#0008a9] rounded-[8px] flex items-center justify-center text-white shrink-0 shadow-sm">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </div>
              <div className="flex flex-col">
                <h2 className="text-sm font-bold text-gray-900 m-0">Scheme Info</h2>
                <span className="text-[11px] text-gray-500">Provide registration & account details</span>
              </div>
            </div>

            <div className="flex flex-col gap-3 mt-1">
              {/* Entry No */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Entry No</label>
                <input
                  ref={entryNoInputRef}
                  readOnly
                  type="text"
                  className="w-full h-[36px] px-3 rounded-[6px] border border-[#cbd5e1] bg-[#f8fafc] text-slate-800 font-bold"
                  value={schemeRegData.entryNo}
                />
              </div>

              {/* Date */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Date <span className="text-red-500">*</span></label>
                <input
                  ref={dateInputRef}
                  type="date"
                  min={minDate}
                  max={maxDate}
                  className="w-full h-[36px] px-3 rounded-[6px] border border-[#cbd5e1]"
                  value={schemeRegData.date}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val && !isDateInFy(val)) {
                      alert("Date incompatible with financial year");
                      return;
                    }
                    setschemeRegData({ ...schemeRegData, date: val });
                  }}
                  onKeyDown={(e) => handleEnterNav(e, "date")}
                />
              </div>

              {/* Scheme Name */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Scheme Name <span className="text-red-500">*</span></label>
                <SherComboBox
                  id="schemeName"
                  comboRef={schemeNameInputRef}
                  disabled={dataFound && hasReceipts}
                  value={schemeRegData.schemeName?.label || ""}
                  onSelect={(opt) => handleSchemeSelect(opt)}
                  onClear={() => handleSchemeSelect(null)}
                  onFreeInput={(txt) => setschemeRegData({ ...schemeRegData, schemeName: { value: null, label: txt } })}
                  options={schemeNames}
                  placeholder="Select scheme name..."
                  showArrow={true}
                  clearable={true}
                  className="w-full h-[36px] outline-none"
                  inputClassName="rounded-[6px] border border-[#cbd5e1]"
                  onKeyDown={(e) => handleEnterNav(e, "schemeName")}
                  onBlur={(e) => handleCheckScheme(e)}
                />
              </div>

              {/* Account Name */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Account Name <span className="text-red-500">*</span></label>
                <SherComboBox
                  id="accountName"
                  comboRef={accountNameInputRef}
                  disabled={dataFound && hasReceipts}
                  value={schemeRegData.accountName?.label || ""}
                  onSelect={(opt) => {
                    setschemeRegData({ ...schemeRegData, accountName: opt });
                    fetchcustomerdetails(opt.value);
                  }}
                  onFreeInput={(txt) => setschemeRegData({ ...schemeRegData, accountName: { value: null, label: txt } })}
                  options={AcNames}
                  placeholder="Select customer..."
                  showArrow={true}
                  clearable={false}
                  className="w-full h-[36px] outline-none"
                  inputClassName="rounded-[6px] border border-[#cbd5e1]"
                  onKeyDown={(e) => handleEnterNav(e, "accountName")}
                  onBlur={(e) => handleCheckCustomer(e)}
                />
              </div>

              {/* Agent */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Agent</label>
                <SherComboBox
                  id="agent"
                  comboRef={agentNameInputRef}
                  value={schemeRegData.agent?.label || ""}
                  onSelect={(opt) => setschemeRegData({ ...schemeRegData, agent: opt })}
                  onFreeInput={(txt) => setschemeRegData({ ...schemeRegData, agent: { value: null, label: txt } })}
                  options={employeeNames}
                  placeholder="Select agent..."
                  showArrow={true}
                  clearable={false}
                  className="w-full h-[36px] outline-none"
                  inputClassName="rounded-[6px] border border-[#cbd5e1]"
                  onKeyDown={(e) => handleEnterNav(e, "agent")}
                />
              </div>

              {/* Duration and Scheme Type */}
              <div className="flex gap-3">
                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[12px] font-bold text-gray-700">
                    Duration ({schemeRegData.type === "Daily" ? "Days" : "Months"})
                  </label>
                  <input
                    ref={durationInputRef}
                    type="number"
                    className="w-full h-[36px] px-3 rounded-[6px] border border-[#cbd5e1]"
                    value={schemeRegData.duration}
                    onChange={(e) => setschemeRegData({ ...schemeRegData, duration: e.target.value })}
                    onKeyDown={(e) => handleEnterNav(e, "duration")}
                  />
                </div>
                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[12px] font-bold text-gray-700">Scheme Type</label>
                  <input
                    type="text"
                    readOnly
                    className="w-full h-[36px] px-3 rounded-[6px] border border-[#cbd5e1] bg-slate-50 text-gray-700 font-medium"
                    value={schemeRegData.type || ""}
                    placeholder="Monthly / Daily"
                  />
                </div>
              </div>

              {/* % Perc */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">% Perc</label>
                <input
                  ref={intperRef}
                  type="text"
                  className="w-full h-[36px] px-3 rounded-[6px] border border-[#cbd5e1]"
                  value={schemeRegData.intperc}
                  onChange={(e) => setschemeRegData({ ...schemeRegData, intperc: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "intperc")}
                />
              </div>

              {/* Account Number */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Account Number</label>
                <div className="flex gap-2">
                  <input
                    ref={accountNoInputRef}
                    type="text"
                    disabled={dataFound && hasReceipts}
                    className={`flex-1 h-[36px] px-3 rounded-[6px] border border-[#cbd5e1] ${
                      dataFound && hasReceipts ? "bg-slate-100 cursor-not-allowed text-slate-500" : ""
                    }`}
                    value={schemeRegData.accountNo}
                    onChange={(e) => setschemeRegData({ ...schemeRegData, accountNo: e.target.value })}
                    onKeyDown={(e) => handleEnterNav(e, "accountNo")}
                  />
                  <button
                    onClick={handleSearchByAccountNo}
                    className="px-4 h-[36px] bg-slate-100 border border-slate-300 rounded-[6px] text-slate-800 text-xs font-bold shrink-0 hover:bg-slate-200"
                  >
                    Search
                  </button>
                </div>
              </div>

              {/* Status Switch */}
              <div className="flex items-center justify-between border-t border-gray-100 pt-3 mt-1">
                <span className="text-[12px] font-bold text-gray-700">Status</span>
                <div className="flex items-center gap-2">
                  <div className="relative inline-block w-9 align-middle select-none">
                    <input
                      type="checkbox"
                      name="accountStatus"
                      id="accountStatus"
                      checked={schemeRegData.isActive}
                      onChange={toggleAccountStatus}
                      className="sr-only peer"
                    />
                    <label
                      htmlFor="accountStatus"
                      className={`block overflow-hidden h-5 rounded-full cursor-pointer transition-colors duration-300 ${
                        schemeRegData.isActive ? 'bg-[#10b981]' : 'bg-[#ef4444]'
                      }`}
                    >
                      <span
                        className={`block h-4 w-4 mt-0.5 ml-0.5 rounded-full bg-white border border-slate-200 shadow-xs transform transition-transform duration-300 ease-in-out ${
                          schemeRegData.isActive ? 'translate-x-4' : 'translate-x-0'
                        }`}
                      />
                    </label>
                  </div>
                  <span className={`text-[12px] font-semibold ${schemeRegData.isActive ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>
                    Active
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "address" && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#0008a9] rounded-[8px] flex items-center justify-center text-white shrink-0 shadow-sm">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
              <div className="flex flex-col">
                <h2 className="text-sm font-bold text-gray-900 m-0">Address Details</h2>
                <span className="text-[11px] text-gray-500">Provide contact information & location</span>
              </div>
            </div>

            <div className="flex flex-col gap-3 mt-1">
              {/* Address 1 */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Address 1</label>
                <input
                  ref={address1InputRef}
                  type="text"
                  className="w-full h-[36px] px-3 rounded-[6px] border border-[#cbd5e1]"
                  value={schemeRegData.add1}
                  onChange={(e) => setschemeRegData({ ...schemeRegData, add1: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "address1")}
                />
              </div>

              {/* Address 2 */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Address 2</label>
                <input
                  ref={address2InputRef}
                  type="text"
                  className="w-full h-[36px] px-3 rounded-[6px] border border-[#cbd5e1]"
                  value={schemeRegData.add2}
                  onChange={(e) => setschemeRegData({ ...schemeRegData, add2: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "address2")}
                />
              </div>

              {/* City */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">City/Town</label>
                <input
                  ref={cityInputRef}
                  type="text"
                  className="w-full h-[36px] px-3 rounded-[6px] border border-[#cbd5e1]"
                  value={schemeRegData.city}
                  onChange={(e) => setschemeRegData({ ...schemeRegData, city: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "city")}
                />
              </div>

              {/* Pincode */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Pincode</label>
                <input
                  ref={pincodeInputRef}
                  type="number"
                  className="w-full h-[36px] px-3 rounded-[6px] border border-[#cbd5e1]"
                  value={schemeRegData.pincode}
                  onChange={(e) => setschemeRegData({ ...schemeRegData, pincode: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "pincode")}
                />
              </div>

              {/* Contact Person */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Contact Person</label>
                <input
                  ref={contactPersonInputRef}
                  type="text"
                  className="w-full h-[36px] px-3 rounded-[6px] border border-[#cbd5e1]"
                  value={schemeRegData.contactPerson}
                  onChange={(e) => setschemeRegData({ ...schemeRegData, contactPerson: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "contactPerson")}
                />
              </div>

              {/* Email */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Email</label>
                <input
                  ref={emailInputRef}
                  type="email"
                  className="w-full h-[36px] px-3 rounded-[6px] border border-[#cbd5e1]"
                  value={schemeRegData.email}
                  onChange={(e) => setschemeRegData({ ...schemeRegData, email: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "email")}
                />
              </div>

              {/* Tele No. */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Tele No.</label>
                <input
                  ref={telNoInputRef}
                  type="number"
                  className="w-full h-[36px] px-3 rounded-[6px] border border-[#cbd5e1]"
                  value={schemeRegData.tele}
                  onChange={(e) => setschemeRegData({ ...schemeRegData, tele: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "telNo")}
                />
              </div>

              {/* Mobile */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Mobile</label>
                <input
                  ref={mobNoInputRef}
                  type="number"
                  className="w-full h-[36px] px-3 rounded-[6px] border border-[#cbd5e1]"
                  value={schemeRegData.mobile}
                  onChange={(e) => setschemeRegData({ ...schemeRegData, mobile: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "mobNo")}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === "nominee" && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#0008a9] rounded-[8px] flex items-center justify-center text-white shrink-0 shadow-sm">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <div className="flex flex-col">
                <h2 className="text-sm font-bold text-gray-900 m-0">Nominee & Proof</h2>
                <span className="text-[11px] text-gray-500">Provide nominee settings & documents</span>
              </div>
            </div>

            <div className="flex flex-col gap-3 mt-1">
              {/* Nominee Name */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Nominee Name</label>
                <input
                  ref={nomineeNameInputRef}
                  type="text"
                  className="w-full h-[36px] px-3 rounded-[6px] border border-[#cbd5e1]"
                  value={schemeRegData.nomineeName}
                  onChange={(e) => setschemeRegData({ ...schemeRegData, nomineeName: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "nomineeName")}
                />
              </div>

              {/* Relation */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Relation</label>
                <input
                  ref={relationInputRef}
                  type="text"
                  className="w-full h-[36px] px-3 rounded-[6px] border border-[#cbd5e1]"
                  value={schemeRegData.relation}
                  onChange={(e) => setschemeRegData({ ...schemeRegData, relation: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "relation")}
                />
              </div>

              {/* Proof Type */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Proof Type</label>
                <SherComboBox
                  id="proofType"
                  comboRef={proofTypeInputRef}
                  value={schemeRegData.proofType?.label || ""}
                  onSelect={(opt) => setschemeRegData({ ...schemeRegData, proofType: opt })}
                  onFreeInput={(txt) => setschemeRegData({ ...schemeRegData, proofType: { value: null, label: txt } })}
                  options={proofTypes}
                  placeholder="Select proof type..."
                  showArrow={true}
                  clearable={false}
                  className="w-full h-[36px] outline-none"
                  inputClassName="rounded-[6px] border border-[#cbd5e1]"
                  onKeyDown={(e) => handleEnterNav(e, "proofType")}
                />
              </div>

              {/* ID Number */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">ID Number</label>
                <input
                  ref={idNumberInputRef}
                  type="text"
                  className="w-full h-[36px] px-3 rounded-[6px] border border-[#cbd5e1]"
                  value={schemeRegData.idNumber}
                  onChange={(e) => setschemeRegData({ ...schemeRegData, idNumber: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "idNumber")}
                />
              </div>

              {/* Nominee Address 1 */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Address 1</label>
                <input
                  ref={nomineeAddress1InputRef}
                  type="text"
                  className="w-full h-[36px] px-3 rounded-[6px] border border-[#cbd5e1]"
                  value={schemeRegData.nomineeAdd1}
                  onChange={(e) => setschemeRegData({ ...schemeRegData, nomineeAdd1: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "nomineeAddress1")}
                />
              </div>

              {/* Nominee Address 2 */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Address 2</label>
                <input
                  ref={nomineeAddress2InputRef}
                  type="text"
                  className="w-full h-[36px] px-3 rounded-[6px] border border-[#cbd5e1]"
                  value={schemeRegData.nomineeAdd2}
                  onChange={(e) => setschemeRegData({ ...schemeRegData, nomineeAdd2: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "nomineeAddress2")}
                />
              </div>

              {/* Front Image upload */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Front Image</label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    className="hidden"
                    ref={proofFrontInputRef}
                    onChange={(e) => handleFileChange(e, 'front')}
                    accept=".jpg,.jpeg,.png,.pdf"
                  />
                  <button
                    ref={proofFrontBrowseRef}
                    type="button"
                    className="h-[36px] px-4 bg-slate-100 border border-slate-300 rounded-[6px] text-slate-800 text-xs font-bold hover:bg-slate-200"
                    onClick={() => proofFrontInputRef.current.click()}
                  >
                    Browse
                  </button>
                  {(schemeRegData.proofFrontFileName || schemeRegData.proofFrontBase64) && (
                    <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg p-1">
                      <button type="button" className="text-blue-600 hover:text-blue-800 text-xs px-1" onClick={() => showFilePreview('front')}>👁️ Preview</button>
                      <button type="button" className="text-emerald-600 hover:text-emerald-800 text-xs px-1" onClick={() => downloadFile('front')}>⬇️ Download</button>
                      <button type="button" className="text-rose-500 hover:text-rose-700 text-xs px-1.5 font-bold" onClick={() => handleRemoveFile('front')}>✕</button>
                    </div>
                  )}
                </div>
              </div>

              {/* Back Image upload */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Back Image</label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    className="hidden"
                    ref={proofBackInputRef}
                    onChange={(e) => handleFileChange(e, 'back')}
                    accept=".jpg,.jpeg,.png,.pdf"
                  />
                  <button
                    ref={proofBackBrowseRef}
                    type="button"
                    className="h-[36px] px-4 bg-slate-100 border border-slate-300 rounded-[6px] text-slate-800 text-xs font-bold hover:bg-slate-200"
                    onClick={() => proofBackInputRef.current.click()}
                  >
                    Browse
                  </button>
                  {(schemeRegData.proofBackFileName || schemeRegData.proofBackBase64) && (
                    <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg p-1">
                      <button type="button" className="text-blue-600 hover:text-blue-800 text-xs px-1" onClick={() => showFilePreview('back')}>👁️ Preview</button>
                      <button type="button" className="text-emerald-600 hover:text-emerald-800 text-xs px-1" onClick={() => downloadFile('back')}>⬇️ Download</button>
                      <button type="button" className="text-rose-500 hover:text-rose-700 text-xs px-1.5 font-bold" onClick={() => handleRemoveFile('back')}>✕</button>
                    </div>
                  )}
                </div>
              </div>

              {/* Narration */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-gray-700">Narration</label>
                <textarea
                  id="narration"
                  ref={narrationInputRef}
                  rows="2"
                  placeholder="Enter scheme notes..."
                  className="w-full border border-[#cbd5e1] rounded-[6px] outline-none px-3 py-2 text-sm resize-none"
                  value={schemeRegData.narration}
                  onChange={(e) => setschemeRegData({ ...schemeRegData, narration: e.target.value })}
                  onKeyDown={(e) => handleEnterNav(e, "narration")}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SchemeRegistrationMob;